import { useCallback } from 'react';
import { confirmsActions } from '@/domain';
import { useSession } from '@/application/usecases/useSession';
import { strings } from '@/shared/strings';
import { runUnasked, useConfirmStore } from '@/ui/primitives/confirmStore';

export interface ConfirmActionOptions {
  /** The sheet's heading; "Confirm" when the action has none of its own. */
  title?: string;
  /** The answer on the confirm button: the action's own verb, "Release", rather than "Yes". */
  confirmLabel?: string;
}

/**
 * Asks before a one-tap action, for the people who are asked.
 *
 * `statement` is the plain question naming the thing, "Release the hold on
 * JO-2047?". `run` sends the command and returns its promise, with whatever
 * follows a success chained on it (the toast, the Undo offer, moving on).
 */
export type AskBeforeAction = (
  statement: string,
  run: () => Promise<unknown>,
  options?: ConfirmActionOptions,
) => void;

/**
 * The one way a one-tap action asks first. The request of 5 October 2026
 * (third), section 2: every action by the accountant, the admin and the
 * receptionist shows a confirmation before it is applied; a technician is asked
 * nothing new.
 *
 * `const ask = useConfirmAction()` and then `ask(statement, run)`. For a role
 * that confirms (`confirmsActions` in the domain, which reads the role and
 * never a permission) the question goes to the shared confirmation, which
 * stays busy until `run` settles. For any other role `run` is called at once.
 * Either way a rejection is an error toast with the server's words, so the
 * caller chains only what follows a success.
 *
 * Named `ask` and not `confirm`: several dialogs already have a `confirm` of
 * their own.
 */
export function useConfirmAction(): AskBeforeAction {
  const role = useSession().data?.role;

  return useCallback<AskBeforeAction>(
    (statement, run, options = {}) => {
      if (!confirmsActions(role)) {
        runUnasked(run);
        return;
      }
      useConfirmStore.getState().ask({
        title: options.title ?? strings.confirm.title,
        statement,
        confirmLabel: options.confirmLabel ?? strings.confirm.yes,
        cancelLabel: strings.confirm.cancel,
        run,
      });
    },
    [role],
  );
}
